import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Chart, registerables } from 'chart.js';
import { SurveyService } from '../../services/survey.service';
import { DataService } from '../../services/data.service';
import { Subscription } from 'rxjs';
import { Survey } from '../../models/survey';
import { DoughnutChartComponent } from "../../shared/doughnut-chart/doughnut-chart.component";
import { BarChartComponent } from "../../shared/bar-chart/bar-chart.component";
import { SURVEY_CONFIG } from '../../../assets/data/survey';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

Chart.register(...registerables);

export interface RankingItem {
  label: string;
  count: number;
}

export interface WonderProfile {
  wonder: string;
  count: number;
  avgAge: number;
  avgSatisfaction: number;
  topNationality: string;
  topSalaryRange: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  imports: [DoughnutChartComponent, BarChartComponent, DecimalPipe, TranslateModule]
})
export class DashboardComponent implements OnInit, OnDestroy {

  private dataService = inject(DataService);
  private dataSubscription!: Subscription;
  public surveys: Survey[] = [];

  isExporting = false;

  exportXlsx(): void {
    this.isExporting = true;
    this.dataService.exportFormulariosXlsx().subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'datos_dashboard.xlsx';
        a.click();
        URL.revokeObjectURL(url);
        this.isExporting = false;
      },
      error: () => {
        this.isExporting = false;
      }
    });
  }

  /** Mapas de traducción value → label */
  private translationMaps: Record<string, Map<string, string>> = {};

  /** Resultados agrupados */
  public nationalityData: number[] = [];
  public nationalityLabels: string[] = [];

  public residenceData: number[] = [];
  public residenceLabels: string[] = [];

  public nationalityRanking: RankingItem[] = [];
  public residenceRanking: RankingItem[] = [];

  public maritalStatusData: number[] = [];
  public maritalStatusLabels: string[] = [];

  public genderData: number[] = [];
  public genderLabels: string[] = [];

  public employmentStatusData: number[] = [];
  public employmentStatusLabels: string[] = [];

  public ageData: number[] = [];
  public ageLabels: string[] = [];

  public salaryRangeData: number[] = [];
  public salaryRangeLabels: string[] = [];

  public visitPurposeData: number[] = [];
  public visitPurposeLabels: string[] = [];

  public averageSatisfaction: number = 0;

  public wondersData: number[] = [];
  public wondersLabels: string[] = [];

  public wonderProfiles: WonderProfile[] = [];

  public firstTimeFuerteventura = { yes: 0, no: 0, pct: 0 };
  public firstTimeLaOliva = { yes: 0, no: 0, pct: 0 };

  private translateService = inject(TranslateService);

  constructor(private surveyService: SurveyService) {
    this.buildTranslationMaps();
  }

  ngOnInit(): void {
    this.dataSubscription = this.surveyService.getSurveys().subscribe(surveys => {
      this.surveys = [...surveys];
      console.log(this.surveys);
      this.processSurveys(this.surveys);
    });
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  /** Construye los mapas de traducción a partir de SURVEY_CONFIG */
  private buildTranslationMaps(): void {
    // Mapas desde las opciones del survey config
    SURVEY_CONFIG.sections.forEach(section => {
      section.questions.forEach(question => {
        if (question.options) {
          const map = new Map<string, string>();
          question.options.forEach(opt => map.set(String(opt.value), opt.label));
          this.translationMaps[question.key] = map;
        }
      });
    });
  }

  /** Traduce un valor crudo a su label legible */
  private translate(field: string, value: string): string {
    const map = this.translationMaps[field];
    const label = map?.get(value) ?? value;
    return this.translateService.instant(label) || label;
  }

  processSurveys(surveys: Survey[]): void {
    const nationalityFull = this.groupBy(surveys, 'nacionalidad');
    const nationalityTop = this.topNWithOthers(nationalityFull, 5);
    this.nationalityLabels = nationalityTop.labels;
    this.nationalityData = nationalityTop.data;
    this.nationalityRanking = nationalityTop.ranking;

    const residenceFull = this.groupBy(surveys, 'residencia');
    const residenceTop = this.topNWithOthers(residenceFull, 5);
    this.residenceLabels = residenceTop.labels;
    this.residenceData = residenceTop.data;
    this.residenceRanking = residenceTop.ranking;

    const maritalMap = this.groupBy(surveys, 'estado_civil');
    this.maritalStatusLabels = maritalMap.labels;
    this.maritalStatusData = maritalMap.data;

    const genderMap = this.groupBy(surveys, 'genero');
    this.genderLabels = genderMap.labels;
    this.genderData = genderMap.data;

    const employmentMap = this.groupBy(surveys, 'situacion_laboral');
    this.employmentStatusLabels = employmentMap.labels;
    this.employmentStatusData = employmentMap.data;

    const visitPurposeMap = this.groupBy(surveys, 'motivo_visita');
    this.visitPurposeLabels = visitPurposeMap.labels;
    this.visitPurposeData = visitPurposeMap.data;

    const ageResult = this.groupByAgeRange(surveys);
    this.ageLabels = ageResult.labels;
    this.ageData = ageResult.data;

    const salaryMap = this.groupBy(surveys, 'rango_salarial');
    this.salaryRangeLabels = salaryMap.labels;
    this.salaryRangeData = salaryMap.data;

    this.averageSatisfaction = this.calculateAverageSatisfaction(surveys);

    const wondersResult = this.groupByWonders(surveys);
    this.wondersLabels = wondersResult.labels;
    this.wondersData = wondersResult.data;
    this.wonderProfiles = this.buildWonderProfiles(surveys);

    this.firstTimeFuerteventura = this.calculateBinary(surveys, 'primera_vez_fuerteventura');
    this.firstTimeLaOliva = this.calculateBinary(surveys, 'primera_vez_la_oliva');
  }

  /** Agrupa los surveys por un campo string, traduce y devuelve labels + data ordenados por count desc */
  private groupBy(surveys: Survey[], field: keyof Survey): { labels: string[], data: number[] } {
    const map = new Map<string, number>();
    surveys.forEach(s => {
      const value = s[field];
      if (value && typeof value === 'string') {
        const label = this.translate(field, value);
        map.set(label, (map.get(label) || 0) + 1);
      }
    });
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    return {
      labels: sorted.map(([label]) => label),
      data: sorted.map(([, count]) => count)
    };
  }

  /** Agrupa top N y el resto como "Otros", devuelve también ranking completo */
  private topNWithOthers(
    result: { labels: string[], data: number[] },
    n: number
  ): { labels: string[], data: number[], ranking: RankingItem[] } {
    const ranking: RankingItem[] = result.labels.map((label, i) => ({ label, count: result.data[i] }));

    if (result.labels.length <= n) {
      return { labels: result.labels, data: result.data, ranking };
    }

    const topLabels = result.labels.slice(0, n);
    const topData = result.data.slice(0, n);
    const othersCount = result.data.slice(n).reduce((a, b) => a + b, 0);
    topLabels.push(this.translateService.instant('DASHBOARD.OTHERS'));
    topData.push(othersCount);

    return { labels: topLabels, data: topData, ranking };
  }

  /** Agrupa edades en rangos de 10 años */
  private groupByAgeRange(surveys: Survey[]): { labels: string[], data: number[] } {
    const ranges = [
      { label: '0-17', min: 0, max: 17 },
      { label: '18-25', min: 18, max: 25 },
      { label: '26-35', min: 26, max: 35 },
      { label: '36-45', min: 36, max: 45 },
      { label: '46-55', min: 46, max: 55 },
      { label: '56-65', min: 56, max: 65 },
      { label: '65+', min: 66, max: 200 }
    ];
    const counts = new Array(ranges.length).fill(0);
    surveys.forEach(s => {
      if (s.edad != null && typeof s.edad === 'number') {
        const idx = ranges.findIndex(r => s.edad! >= r.min && s.edad! <= r.max);
        if (idx !== -1) counts[idx]++;
      }
    });
    return { labels: ranges.map(r => r.label), data: counts };
  }

  private readonly wonderLabelsMap: Record<string, string> = {
    'evento': 'Evento',
    'playa': 'Playa',
    'arquitectura': 'Arquitectura',
    'pueblo': 'Pueblo'
  };

  private readonly salaryLabelsMap: Record<string, string> = {
    '< 20.000€': '< 20k€',
    '20.001€ - 40.000€': '20-40k€',
    '40.001€ - 80.000€': '40-80k€',
    '> 80.000€': '> 80k€'
  };

  private groupByWonders(surveys: Survey[]): { labels: string[], data: number[] } {
    const keys = ['evento', 'playa', 'arquitectura', 'pueblo'];
    const counts = keys.map(key =>
      surveys.filter(s => s.maravillas_visitadas && s.maravillas_visitadas.includes(key)).length
    );
    return {
      labels: keys.map(k => this.wonderLabelsMap[k]),
      data: counts
    };
  }

  private buildWonderProfiles(surveys: Survey[]): WonderProfile[] {
    const keys = ['evento', 'playa', 'arquitectura', 'pueblo'];
    return keys.map(key => {
      const filtered = surveys.filter(s => s.maravillas_visitadas && s.maravillas_visitadas.includes(key));
      const count = filtered.length;

      const ages = filtered.filter(s => s.edad != null).map(s => s.edad!);
      const avgAge = ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 0;

      const sats = filtered.filter(s => s.satisfaccion != null).map(s => s.satisfaccion!);
      const avgSat = sats.length > 0 ? sats.reduce((a, b) => a + b, 0) / sats.length : 0;

      const natMap = new Map<string, number>();
      filtered.forEach(s => {
        if (s.nacionalidad) {
          const label = this.translate('nacionalidad', s.nacionalidad);
          natMap.set(label, (natMap.get(label) || 0) + 1);
        }
      });
      const topNat = natMap.size > 0
        ? Array.from(natMap.entries()).sort((a, b) => b[1] - a[1])[0][0]
        : '-';

      const salMap = new Map<string, number>();
      filtered.forEach(s => {
        if (s.rango_salarial != null) {
          salMap.set(s.rango_salarial, (salMap.get(s.rango_salarial) || 0) + 1);
        }
      });
      const topSal = salMap.size > 0
        ? this.salaryLabelsMap[Array.from(salMap.entries()).sort((a, b) => b[1] - a[1])[0][0]] || Array.from(salMap.entries()).sort((a, b) => b[1] - a[1])[0][0]
        : '-';

      return {
        wonder: this.wonderLabelsMap[key],
        count,
        avgAge: Math.round(avgAge),
        avgSatisfaction: Math.round(avgSat * 10) / 10,
        topNationality: topNat,
        topSalaryRange: topSal
      };
    });
  }

  private calculateAverageSatisfaction(surveys: Survey[]): number {
    const valid = surveys.filter(s => s.satisfaccion != null && typeof s.satisfaccion === 'number');
    if (valid.length === 0) return 0;
    const sum = valid.reduce((acc, s) => acc + s.satisfaccion!, 0);
    return sum / valid.length;
  }

  private calculateBinary(surveys: Survey[], field: keyof Survey): { yes: number, no: number, pct: number } {
    const valid = surveys.filter(s => s[field] != null);
    const yes = valid.filter(s => s[field] === true).length;
    const no = valid.length - yes;
    const pct = valid.length > 0 ? Math.round((yes / valid.length) * 1000) / 10 : 0;
    return { yes, no, pct };
  }

  getSurveysLength(): number {
    return this.surveys.length;
  }
}
